// src/services/graphService.js
import { msalInstance } from "../index";
import { loginRequest, GRAPH_BASE, GRAPH_BETA, ORG_CONFIG } from "../authConfig";

// ─── Token acquisition ────────────────────────────────────────────────────────
async function getToken() {
  const account = msalInstance.getActiveAccount();
  if (!account) throw new Error("No active account. Please sign in.");
  const response = await msalInstance.acquireTokenSilent({
    ...loginRequest,
    account,
  });
  return response.accessToken;
}

export async function graphRequest(url, options = {}) {
  const token = await getToken();
  const res = await fetch(url, {
    ...options,
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
      ...(options.headers || {}),
    },
  });
  if (!res.ok) {
    // Try to parse error body, fall back to status text
    const text = await res.text();
    let errMsg = `Graph API error: ${res.status}`;
    try {
      const err = JSON.parse(text);
      errMsg = err?.error?.message || errMsg;
    } catch { /* ignore parse error, use status */ }
    throw new Error(errMsg);
  }
  // 204 No Content and 202 Accepted return no body
  if (res.status === 204 || res.status === 202) return null;
  // Some responses may have empty body even with 200
  const text = await res.text();
  if (!text || text.trim() === "") return null;
  try {
    return JSON.parse(text);
  } catch {
    return null; // Non-JSON response — don't throw
  }
}

// ─── User / Org ───────────────────────────────────────────────────────────────
export async function getCurrentUser() {
  return graphRequest(`${GRAPH_BASE}/me?$select=displayName,mail,userPrincipalName`);
}

export async function getAllUsers() {
  // Paginate through all users — Graph returns max 100 per page
  let users = [];
  let url = `${GRAPH_BASE}/users?$select=id,displayName,mail,userPrincipalName&$top=100&$filter=accountEnabled eq true and userType eq 'Member'`;
  
  while (url) {
    const data = await graphRequest(url);
    const page = data?.value || [];
    users = users.concat(page);
    // Follow @odata.nextLink for next page
    url = data?.["@odata.nextLink"] || null;
    // Safety cap at 5000 users
    if (users.length >= 5000) break;
  }
  return users;
}

// ─── MODULE 1: Phishing Purge ─────────────────────────────────────────────────

export async function searchMailboxes({ sender, subject, msgId, keywords, dateFrom, dateTo }) {
  // Step 1: Trigger the Runbook (Compliance Search across all 2100+ mailboxes)
  const trigger = await callFunction("search-mailboxes", { sender, subject, msgId, keywords, dateFrom, dateTo });
  if (!trigger?.runbookJobId) throw new Error("Failed to start mailbox search");

  const { runbookJobId, pollAfterSeconds = 90 } = trigger;

  // Step 2: Wait then poll — org-wide search takes 8-12 minutes for 2000+ mailboxes
  // 90s initial wait + 20 polls × 30s = up to 13.5 minutes total
  await new Promise(r => setTimeout(r, pollAfterSeconds * 1000));

  for (let attempt = 0; attempt < 20; attempt++) {
    const result = await callFunction("search-mailboxes", { pollRunbookJobId: runbookJobId });
    if (result?.status === "complete") return result.results || [];
    if (result?.status === "error") throw new Error(result.error || "Search failed");
    if (attempt < 19) await new Promise(r => setTimeout(r, 30000));
  }

  throw new Error("Search timed out — the Runbook may still be running. Check Azure Automation Jobs and try again.");
}

export async function deleteMessage(userId, messageId) {
  // Permanently delete (not soft delete)
  await graphRequest(`${GRAPH_BASE}/users/${userId}/messages/${messageId}`, {
    method: "DELETE",
  });
}

export async function purgeMessages(matches) {
  // Use Azure Function for app-only auth deletion across all mailboxes
  const data = await callFunction("delete-messages", { matches });
  return data?.results || matches.map(m => ({ ...m, status: "failed", error: "Function call failed" }));
}

// ─── MODULE 2: Block List ─────────────────────────────────────────────────────

export async function addToBlockList(sender, type = "sender") {
  // Uses Microsoft Defender Tenant Allow/Block List via Security API
  const body = {
    listType: type === "domain" ? "domain" : "sender",
    action: "block",
    entries: [{ value: sender }],
    noExpiry: true,
  };
  return graphRequest(`${GRAPH_BETA}/security/attackSimulation`, {
    method: "POST",
    body: JSON.stringify(body),
  }).catch(() => {
    // Fallback: use Exchange transport rule via Graph
    console.warn("Defender API unavailable, logging block request for manual action");
    return { fallback: true, value: sender };
  });
}

export async function getBlockList() {
  return graphRequest(`${GRAPH_BETA}/security/threatSubmission/emailThreats?$top=50`).catch(
    () => ({ value: [] })
  );
}

// ─── MODULE 3: Mailbox Delegation ─────────────────────────────────────────────

// Azure Function URL — set after deploying the function app
export const FUNCTION_APP_URL = "https://ima-response-functions-fud2d0cwhechfyf3.centralus-01.azurewebsites.net";
export const FUNCTION_KEY = "OvFAxromVEnpQIFXrZi_7bujcDKMKGWLqNDLpvQsCuoqAzFu_Nwb9w==";

// Helper to call Azure Functions
async function callFunction(path, body) {
  const res = await fetch(`${FUNCTION_APP_URL}/api/${path}?code=${FUNCTION_KEY}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  const text = await res.text();
  if (!res.ok) {
    let msg = `Function error: ${res.status}`;
    try { msg = JSON.parse(text)?.error || msg; } catch {}
    throw new Error(msg);
  }
  try { return JSON.parse(text); } catch { return null; }
}

export async function grantDelegation(targetEmail, granteeEmail, hours, accessLevel = "rw", reason = "", ticketRef = "") {
  const token = await getToken();

  // Call Azure Function which runs Exchange Online permission grant server-side
  const res = await fetch(`${FUNCTION_APP_URL}/api/delegate-mailbox?code=${FUNCTION_KEY}`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      // Pass user's token so function can verify the caller is authenticated
      "X-Caller-Token": token,
    },
    body: JSON.stringify({
      targetEmail,
      granteeEmail,
      accessLevel,
      hours,
      reason,
      ticketRef,
      operatorEmail: granteeEmail,
    }),
  });

  const text = await res.text();
  if (!res.ok) {
    let msg = `Delegation failed: ${res.status}`;
    try { msg = JSON.parse(text)?.error || msg; } catch {}
    throw new Error(msg);
  }

  const data = JSON.parse(text);
  return {
    targetEmail: data.targetEmail,
    targetDisplayName: data.targetDisplayName,
    granteeEmail: data.granteeEmail,
    accessLevel: data.accessLevel,
    hours: data.hours,
    expiry: data.expiry,
    granted: data.granted,
    delegationGranted: true,
  };
}

export async function revokeDelegation(targetEmail, granteeEmail, operatorEmail, ticketRef) {
  const res = await fetch(`${FUNCTION_APP_URL}/api/revoke-delegation?code=${FUNCTION_KEY}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ targetEmail, granteeEmail, operatorEmail, ticketRef }),
  });

  const text = await res.text();
  if (!res.ok) {
    let msg = `Revoke failed: ${res.status}`;
    try { msg = JSON.parse(text)?.error || msg; } catch {}
    throw new Error(msg);
  }
  return JSON.parse(text);
}

export async function getActiveDelegations() {
  // Read active delegations from SharePoint list
  return getSharePointListItems("DelegationLog", "$filter=fields/Status eq 'Active'");
}

// ─── MODULE 4: Exfiltration Audit ────────────────────────────────────────────

export async function getAuditLogs(userId, startTime, endTime) {
  const filter = [
    `activityDateTime ge ${startTime}`,
    `activityDateTime le ${endTime}`,
    userId ? `userId eq '${userId}'` : null,
  ].filter(Boolean).join(" and ");

  return graphRequest(
    `${GRAPH_BASE}/auditLogs/signIns?$filter=${encodeURIComponent(filter)}&$top=200`
  );
}

export async function getMailActivityReport(targetEmail, days = 1) {
  const since = new Date(Date.now() - days * 86400000).toISOString();
  const personalDomains = ["gmail.com", "yahoo.com", "hotmail.com", "protonmail.com", "icloud.com", "aol.com", "live.com", "msn.com", "me.com"];

  // For org-wide scan, use Azure Function (app-only auth)
  if (targetEmail === "all") {
    const data = await callFunction("exfil-scan", { targetEmail: "all", days });
    return { messages: [], flags: data?.flags || [] };
  }

  // Single user scan — use delegated auth (user scanning their own or accessible mailbox)
  const allFlags = [];
  const allMessages = [];
  const upn = targetEmail;

  try {
    const url = `${GRAPH_BASE}/users/${encodeURIComponent(upn)}/mailFolders/SentItems/messages?$select=id,subject,toRecipients,ccRecipients,bccRecipients,sentDateTime,hasAttachments,bodyPreview&$top=100`;
    const data = await graphRequest(url);
    const messages = (data?.value || []).filter(m => new Date(m.sentDateTime) >= new Date(since));

    for (const msg of messages) {
      const recipients = [
        ...(msg.toRecipients || []),
        ...(msg.ccRecipients || []),
        ...(msg.bccRecipients || []),
      ].map(r => r.emailAddress?.address?.toLowerCase()).filter(Boolean);

      const external = recipients.filter(r => !r.endsWith(`@${ORG_CONFIG.domain}`));
      const personal = external.filter(r => personalDomains.some(d => r.endsWith(`@${d}`)));

      if (personal.length > 0) allFlags.push({ type: "personal_domain", severity: "medium", msg, recipients: personal, user: upn });
      if (external.length > 5) allFlags.push({ type: "bulk_external", severity: "high", msg, count: external.length, recipients: external, user: upn });
      if (msg.hasAttachments && external.length > 0) allFlags.push({ type: "attachment_external", severity: "medium", msg, recipients: external, user: upn });
    }
    allMessages.push(...messages);
  } catch (err) {
    // If delegated access fails for this user, try via function
    const data = await callFunction("exfil-scan", { targetEmail, days });
    return { messages: [], flags: data?.flags || [] };
  }

  return { messages: allMessages, flags: allFlags };
}

// ─── MODULE 5: Message Trace ──────────────────────────────────────────────────

export async function runMessageTrace({ sender, recipient, subject, dateFrom, dateTo }) {
  // Step 1: Trigger the Runbook
  const trigger = await callFunction("message-trace", { sender, recipient, subject, dateFrom, dateTo });
  if (!trigger?.runbookJobId) throw new Error("Failed to start message trace");

  const { runbookJobId, pollAfterSeconds = 90 } = trigger;

  // Step 2: Wait then poll job output (max 6 attempts, 15s apart)
  await new Promise(r => setTimeout(r, pollAfterSeconds * 1000));

  for (let attempt = 0; attempt < 6; attempt++) {
    const result = await callFunction("message-trace", { pollRunbookJobId: runbookJobId });
    if (result?.status === "complete") return result.messages || [];
    if (result?.status === "error") throw new Error(result.error || "Message trace failed");
    if (attempt < 5) await new Promise(r => setTimeout(r, 15000));
  }

  throw new Error("Message trace timed out — try again in a moment.");
}

// ─── MODULE 6: SharePoint Audit Logging ──────────────────────────────────────

async function getSiteId() {
  const data = await graphRequest(
    `${GRAPH_BASE}/sites/${ORG_CONFIG.sharepointSite}:/sites/${ORG_CONFIG.sharepointSiteName}`
  );
  return data.id;
}

async function getListId(siteId, listName) {
  const data = await graphRequest(
    `${GRAPH_BASE}/sites/${siteId}/lists?$filter=displayName eq '${listName}'`
  );
  return data?.value?.[0]?.id;
}

export async function writeAuditLog(entry) {
  // Write to local in-memory log immediately for UI visibility
  const localEntry = {
    timestamp: new Date().toLocaleString(),
    module: entry.module,
    action: entry.action,
    operatorEmail: entry.operatorEmail,
    ticketRef: entry.ticketRef || "",
    matchCount: entry.matchCount,
    successCount: entry.successCount,
    failCount: entry.failCount,
    severity: entry.severity || "info",
    source: "local",
  };
  try {
    const { localAuditLog } = await import("../pages/Pages");
    localAuditLog.unshift(localEntry);
    if (localAuditLog.length > 200) localAuditLog.pop();
  } catch {}

  // Write to SharePoint via Azure Function (app-only auth)
  callFunction("write-audit-log", entry).catch(err =>
    console.warn("Audit log write failed:", err.message)
  );
}

async function createAuditList(siteId) {
  const body = {
    displayName: ORG_CONFIG.sharepointListName,
    columns: [
      { name: "Module", text: {} },
      { name: "Operator", text: {} },
      { name: "OperatorEmail", text: {} },
      { name: "TicketRef", text: {} },
      { name: "Details", text: { allowMultipleLines: true } },
      { name: "Severity", text: {} },
      { name: "Timestamp", dateTime: {} },
      { name: "MatchCount", number: {} },
      { name: "SuccessCount", number: {} },
      { name: "FailCount", number: {} },
    ],
    list: { template: "genericList" },
  };
  return graphRequest(`${GRAPH_BASE}/sites/${siteId}/lists`, {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export async function getSharePointListItems(listName, filter = "") {
  try {
    const siteId = await getSiteId();
    const listId = await getListId(siteId, listName);
    if (!listId) return [];
    const url = `${GRAPH_BASE}/sites/${siteId}/lists/${listId}/items?expand=fields&$top=100${filter ? `&${filter}` : ""}`;
    const data = await graphRequest(url);
    return data?.value || [];
  } catch {
    return [];
  }
}

// ─── Audit Email ──────────────────────────────────────────────────────────────

export async function sendAuditEmail(entry) {
  const body = {
    message: {
      subject: `[IMA Response Suite] ${entry.module} — ${entry.action}`,
      body: {
        contentType: "HTML",
        content: buildEmailHtml(entry),
      },
      toRecipients: [
        { emailAddress: { address: ORG_CONFIG.auditEmail } },
      ],
    },
    saveToSentItems: false,
  };

  return graphRequest(`${GRAPH_BASE}/me/sendMail`, {
    method: "POST",
    body: JSON.stringify(body),
  });
}

function buildEmailHtml(entry) {
  return `
    <div style="font-family:monospace;font-size:13px;color:#1a1a2e;max-width:600px">
      <div style="background:#0d0f12;padding:16px 20px;border-radius:6px 6px 0 0">
        <span style="color:#e05c2e;font-weight:600;font-size:15px">IMA Response Suite</span>
        <span style="color:#8892a0;margin-left:12px;font-size:12px">${entry.module}</span>
      </div>
      <div style="border:1px solid #e5e7eb;border-top:none;padding:20px;border-radius:0 0 6px 6px">
        <table style="width:100%;border-collapse:collapse;font-size:12px">
          <tr><td style="padding:5px 0;color:#6b7280;width:140px">Action</td><td style="padding:5px 0;font-weight:500">${entry.action}</td></tr>
          <tr><td style="padding:5px 0;color:#6b7280">Operator</td><td style="padding:5px 0">${entry.operatorEmail}</td></tr>
          <tr><td style="padding:5px 0;color:#6b7280">Timestamp</td><td style="padding:5px 0">${new Date().toLocaleString()}</td></tr>
          ${entry.ticketRef ? `<tr><td style="padding:5px 0;color:#6b7280">Ticket ref</td><td style="padding:5px 0">${entry.ticketRef}</td></tr>` : ""}
          ${entry.matchCount !== undefined ? `<tr><td style="padding:5px 0;color:#6b7280">Matches found</td><td style="padding:5px 0">${entry.matchCount}</td></tr>` : ""}
          ${entry.successCount !== undefined ? `<tr><td style="padding:5px 0;color:#6b7280">Successfully actioned</td><td style="padding:5px 0;color:#059669;font-weight:500">${entry.successCount}</td></tr>` : ""}
          ${entry.failCount ? `<tr><td style="padding:5px 0;color:#6b7280">Failed</td><td style="padding:5px 0;color:#dc2626">${entry.failCount}</td></tr>` : ""}
        </table>
        ${entry.details ? `<div style="margin-top:14px;padding:10px;background:#f9fafb;border-radius:4px;font-size:11px;color:#374151"><pre style="margin:0;white-space:pre-wrap">${JSON.stringify(entry.details, null, 2)}</pre></div>` : ""}
      </div>
      <div style="margin-top:10px;font-size:10px;color:#9ca3af">Logged to SharePoint: imagroupmgmt.sharepoint.com/sites/ITSec</div>
    </div>
  `;
}

// ─── Threat Intelligence ──────────────────────────────────────────────────────

export async function checkThreatIntel(sender, domain, subject) {
  const history = await getSharePointListItems(
    ORG_CONFIG.sharepointListName,
    `$filter=fields/Module eq 'Purge'&$orderby=fields/Timestamp desc&$top=200`
  );

  const matches = [];
  for (const item of history) {
    const details = JSON.parse(item.fields?.Details || "{}");
    if (
      (sender && details.sender === sender) ||
      (domain && details.sender?.endsWith(`@${domain}`)) ||
      (subject && details.subject && details.subject.toLowerCase().includes(subject.toLowerCase()))
    ) {
      matches.push({
        date: item.fields?.Timestamp,
        operator: item.fields?.OperatorEmail,
        action: item.fields?.Title,
        ticketRef: item.fields?.TicketRef,
        count: item.fields?.MatchCount,
      });
    }
  }

  return matches;
}

// ─── MODULE 7: User Account Management ───────────────────────────────────────

/**
 * Revoke all active sign-in sessions for a user.
 * Forces re-authentication on all devices and apps immediately.
 */
export async function revokeUserSessions(userId) {
  return graphRequest(`${GRAPH_BASE}/users/${userId}/revokeSignInSessions`, {
    method: "POST",
  });
}

/**
 * Revoke all MFA / FIDO2 / passwordless sessions.
 * Invalidates refresh tokens associated with MFA claims.
 */
export async function revokeUserMfaSessions(userId) {
  // Invalidate all refresh tokens (covers MFA session tokens)
  await graphRequest(`${GRAPH_BASE}/users/${userId}/revokeSignInSessions`, {
    method: "POST",
  });

  // Also revoke individual auth method sessions via beta endpoint
  return graphRequest(
    `${GRAPH_BETA}/users/${userId}/authentication/microsoftAuthenticatorMethods`,
    { method: "GET" }
  )
    .then(async (methods) => {
      // List and revoke each authenticator session
      const revocations = (methods?.value || []).map((m) =>
        graphRequest(
          `${GRAPH_BETA}/users/${userId}/authentication/microsoftAuthenticatorMethods/${m.id}/device/registeredOwners`,
          { method: "GET" }
        ).catch(() => null)
      );
      await Promise.allSettled(revocations);
      return { sessionsRevoked: true, methodCount: methods?.value?.length || 0 };
    })
    .catch(() => ({ sessionsRevoked: true, methodCount: 0 }));
}

/**
 * Disable a user account — prevents all sign-ins immediately.
 * Account data and mailbox are preserved.
 */
export async function disableUserAccount(userId) {
  return graphRequest(`${GRAPH_BASE}/users/${userId}`, {
    method: "PATCH",
    body: JSON.stringify({ accountEnabled: false }),
  });
}

/**
 * Re-enable a previously disabled user account.
 */
export async function enableUserAccount(userId) {
  return graphRequest(`${GRAPH_BASE}/users/${userId}`, {
    method: "PATCH",
    body: JSON.stringify({ accountEnabled: true }),
  });
}

/**
 * Get a user by UPN or email for the account management UI.
 */
export async function getUserByEmail(email) {
  const data = await callFunction("user-lookup", { query: email });
  return data?.users?.[0] || null;
}

export async function searchUsersByName(query) {
  const data = await callFunction("user-lookup", { query });
  return data?.users || [];
}

/**
 * Get recent sign-in activity for a user (last 10 sign-ins).
 */
export async function getUserSignInActivity(userId) {
  // Try via Graph directly (requires AuditLog.Read.All delegated)
  try {
    const data = await graphRequest(
      `${GRAPH_BASE}/auditLogs/signIns?$filter=userId eq '${userId}'&$orderby=createdDateTime desc&$top=10&$select=createdDateTime,appDisplayName,ipAddress,location,status,conditionalAccessStatus`
    );
    return data || { value: [] };
  } catch {
    return { value: [], unavailable: true };
  }
}

/**
 * Run all three containment actions at once:
 * 1. Revoke active sessions
 * 2. Revoke MFA sessions
 * 3. Disable account
 */
export async function fullContainUser(userId, userEmail, operator, ticketRef) {
  const results = { sessions: null, mfa: null, disabled: null, errors: [] };

  try {
    results.sessions = await revokeUserSessions(userId);
  } catch (e) {
    results.errors.push(`Session revoke: ${e.message}`);
  }

  try {
    results.mfa = await revokeUserMfaSessions(userId);
  } catch (e) {
    results.errors.push(`MFA revoke: ${e.message}`);
  }

  try {
    results.disabled = await disableUserAccount(userId);
  } catch (e) {
    results.errors.push(`Account disable: ${e.message}`);
  }

  // Audit log
  const entry = {
    module: "User containment",
    action: `Full containment: sessions revoked, MFA revoked, account disabled — ${userEmail}`,
    operator: operator?.displayName,
    operatorEmail: operator?.mail,
    ticketRef,
    details: { userEmail, userId, actions: ["revokeSessionss", "revokeMFA", "disableAccount"] },
    severity: "high",
    matchCount: 1,
    successCount: results.errors.length === 0 ? 1 : 0,
    failCount: results.errors.length > 0 ? 1 : 0,
  };

  await Promise.allSettled([writeAuditLog(entry), sendAuditEmail(entry)]);
  return results;
}

// ─── ADDITIONS TO src/services/graphService.js ────────────────────────────────
// Paste these functions anywhere after the existing exports.
// No new imports needed — graphRequest, GRAPH_BASE, GRAPH_BETA are already there.
// The required scope (UserAuthenticationMethod.ReadWrite.All) is already in authConfig.js.

// ── Get all registered MFA methods for a user (excludes password) ─────────────
export async function getUserMFAMethods(userId) {
  const data = await graphRequest(
    `${GRAPH_BASE}/users/${userId}/authentication/methods`
  );
  return (data?.value || []).filter(
    m => m["@odata.type"] !== "#microsoft.graph.passwordAuthenticationMethod"
  );
}

// ── Get per-user MFA state: "disabled" | "enabled" | "enforced" ───────────────
// Uses the beta endpoint — perUserMfaState is not available in v1.0
export async function getUserMFAState(userId) {
  const data = await graphRequest(
    `${GRAPH_BETA}/users/${userId}/authentication/requirements`
  );
  return data?.perUserMfaState || "disabled";
}

// ── Set per-user MFA state ────────────────────────────────────────────────────
// state: "enabled" | "disabled" | "enforced"
export async function setUserMFAState(userId, state) {
  await graphRequest(
    `${GRAPH_BETA}/users/${userId}/authentication/requirements`,
    {
      method: "PATCH",
      body: JSON.stringify({ perUserMfaState: state }),
    }
  );
}
